import { useEffect } from 'react'

import {
  CollectionForMetadata_collection,
  CollectionPageContents_me,
} from 'globalTypes'

import { useQuery } from '@apollo/client'
import ShareWrapper from 'Root/App/Card/Collection/ShareWrapper'
import { getMenuItems } from 'Root/App/Card/Collection/utilities'
import AssociationFolderContext from 'Root/App/Contexts/AssociationFolderContext'
import Context from 'Root/App/Menu/Context'
import Loading from 'Root/Loader/Page'
import PageNotFound from 'Root/Pages/PageNotFound'
import mixpanelTrack from 'lib/mixpanel-track'
import { useCurrentUserId } from 'lib/use-current-user-id'
import first from 'lodash/first'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import { useRouter } from 'next/router'

import { userElementAuthorization } from '../../../lib/userElementAuthorization'
import AssociationContext from './Component/AssociationContext'
import CollectionHeader from './Component/Header'
import Page from './Component/Page'
import ContactContext from './Component/Selling/ContactContext'
import COLLECTION_PAGE_CONTENTS from './collection-page-contents.graphql'

const Collection = ({
  ssCollection,
  ssMe,
}: {
  ssCollection: CollectionForMetadata_collection
  ssMe: CollectionPageContents_me
}) => {
  const currentUserId = useCurrentUserId()
  const { identifier, token } = useRouter().query
  const { push } = useRouter()

  const { data, loading, refetch } = useQuery(COLLECTION_PAGE_CONTENTS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      identifier: isArray(identifier) ? first(identifier) : identifier,
      token: (isArray(token) ? first(token) : token) || null,
      userId: currentUserId,
    },
  })

  useEffect(() => {
    if (!data?.collection) {
      return
    }

    const mixpanelCourseEventName = data.collection.canManage
      ? 'Corso proprio visitato'
      : 'Corso visitato'

    mixpanelTrack(mixpanelCourseEventName, {
      nome_corso: data.collection.title,
      id_corso: data.collection.slug,
      autore: data.collection.creator.vanity_name,
      visibilita: data.collection.visibility,
      in_vendita: data.collection.selling,
    })
  }, [!loading])

  if (loading) {
    return (
      <ShareWrapper collection={ssCollection}>
        <CollectionHeader collection={ssCollection} me={ssMe} />
        <Loading />
      </ShareWrapper>
    )
  }

  if (!Boolean(data?.collection) || isEmpty(data?.collection)) {
    return <PageNotFound />
  }

  const collection = data.collection

  const userIsCreator = collection.creator.id === currentUserId

  if (
    !userIsCreator &&
    !userElementAuthorization(
      collection.canKeepAccess,
      collection.creator.subscriptionPlan.code
    )
  ) {
    mixpanelTrack(`Errore: Tentativo di visualizzazione corso`, {
      id_corso: collection.slug,
      nome_corso: collection.title,
      visibilita_corso: collection.visibility,
      email_creator: collection.creator.email,
      autore: collection.creator.vanity_name,
    })

    push(
      {
        pathname: '/not-available',
        query: {
          identifier: collection.creator.id,
        },
      },
      '/not-available'
    )
  }

  const menuItems = getMenuItems(collection, false, userIsCreator)

  const totalUserContents = data?.me?.contents?.pageInfo?.total || 0

  return (
    <ShareWrapper collection={collection} refetch={refetch}>
      <AssociationContext
        refetch={refetch}
        totalUserContents={totalUserContents}
      >
        <AssociationFolderContext>
          <ContactContext collectionId={collection.id}>
            <Context.Provider value={menuItems}>
              <CollectionHeader collection={collection} me={data?.me} />
              <Page collection={collection} me={data?.me} refetch={refetch} />
            </Context.Provider>
          </ContactContext>
        </AssociationFolderContext>
      </AssociationContext>
    </ShareWrapper>
  )
}

export default Collection
